import { lighten } from 'polished';
import { css, html } from 'react-strict-dom';
import { Text } from 'src/components';

import { Color } from '../../../constants';

type BaseProps = {
  label: string;
  bordered?: boolean;
  regular?: boolean;
};

type Props =
  | (BaseProps & {
      color?: string;
    })
  | (BaseProps & {
      bgColor: string;
      textColor: string;
    });

/**
 * TicketBadge component can be used in two ways:
 * 1. With a color prop - it will use this for the text color with slightly lighter background:
 *    <TicketBadge label="Label" color="#000000" />
 * 2. With a bgColor and textColor props:
 *    <TicketBadge label="Label" bgColor="#000000" textColor="#ffffff" />
 */
export default function TicketBadge(props: Props) {
  const { label, bordered, regular } = props;

  let backgroundColor = Color.neutral0;
  let textColor = Color.neutral100;
  if ('bgColor' in props) {
    const { bgColor, textColor: customTextColor } = props;
    backgroundColor = bgColor;
    textColor = customTextColor;
  } else {
    const { color } = props;
    backgroundColor = color ? lighten(0.42, color) : '#fff';
    textColor = color ? color : '#1f2937';
  }
  return (
    <html.span
      style={[
        styles.badge,
        styles.colors(textColor, backgroundColor),
        bordered && styles.bordered(textColor),
      ]}
    >
      <Text typeface={regular ? 'body.regular' : 'body.medium'}>{label}</Text>
    </html.span>
  );
}

const styles = css.create({
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 8,
    paddingInline: 6,
    paddingBlock: 2,
    fontSize: 13,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
  },
  colors: (color: string, backgroundColor: string) => ({
    color,
    backgroundColor,
  }),
  bordered: (borderColor: string) => ({
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor,
  }),
});
